import Image from "next/legacy/image";
import Link from "next/link";
import { useRouter } from "next/router";
import { useAtom, useAtomValue } from "jotai";
import SearchIcon from "@mui/icons-material/Search";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import { Box, Drawer, FormControl, IconButton, InputAdornment, OutlinedInput, useMediaQuery, Button, Typography, CircularProgress, Tooltip, Dialog, DialogContent, DialogActions } from "@mui/material";
import ChatBubbleOutlineRoundedIcon from "@mui/icons-material/ChatBubbleOutlineRounded";
import { CSSProperties, FC, KeyboardEventHandler, memo, useState, useRef, useEffect, useMemo, useCallback } from "react";
import { SEARCH_STEP_S, SEARCH_WORDS, searchWordDelayS } from "./searchWords";

import { findInstruments, fromFindInstrument, searchOmni } from "@/controllers/find-instruments";
import { isOmnisearchEnabled, searchDelayMs } from "@/lib/omnisearch/flag";
import { LatestSearch, mergeResults } from "@/lib/omnisearch/client";
import { SearchRum } from "@/lib/omnisearch/rum";
import { FindInstruments } from "@/models/find-instruments";
import HeaderAutocomplete from "@/components/header-autocomplete/HeaderAutocomplete";
import OmniAutocomplete, { routeForResult } from "@/components/omni-autocomplete/OmniAutocomplete";
import { recordSearchClick } from "@/lib/omnisearch/click";
import { browserStockTarget, createSearchPreloader, SearchPreloader, StockTarget } from "@/lib/omnisearch/stock-preload";
import { OmniResult } from "@/lib/omnisearch/types";
import UniverseSelectBase from "@/components/universe-select/UniverseSelect";
import { themeState, userState, backgroundColorState, greyDarkerState, whiteMediumState, textColorState, greyLighterState, ortexColorState, chatMinimizedState, chatModalState } from "@/lib/store";
import Menu from "../menu/Menu";
import UserMenu from "../user-menu/UserMenu";
import Notifications from "../notifications/Notifications";
import HeaderCustomViews from "../header-custom-views/HeaderCustomViews";

import styles from "./Header.module.scss";
import chatInputStyles from "../chat-input/ChatInput.module.scss";
import { Stock } from "@/models/stock";
import { classNames } from "@/lib/utils";
import { getPreferredTabSuffix, readPreferredStockTab } from "@/lib/stock-tabs";
import useAppSubscriberUpgrade from "src/hooks/useAppSubscriberUpgrade";
import { UPGRADE_LOCATIONS, trackUpgradeClick } from "@/lib/tracking";
import dynamic from "next/dynamic";
import { useTradingVisible } from "@/lib/trading/useTradingAccess";

/** What one keystroke's lookup found: omnisearch rows, or the legacy endpoint's when omnisearch had none. */
type SearchAnswer = { omni: OmniResult[] } | { legacy: FindInstruments[] };

const OMNI_LIMIT = 15;

// The local tier (the measure vocabulary and its Web Worker) loads with the first keystroke, not with every page:
// its 450 phrases were evaluated in the shell on every load, and a page nobody searches on never needs them.
const searchLocalSlice = (text: string, limit: number): Promise<OmniResult[]> => import("@/lib/omnisearch/local").then((local) => local.searchLocalSlice(text, limit)).catch((): OmniResult[] => []);

/**
 * The whole server lookup for one text, as one unit for `LatestSearch`: omnisearch, then
 * `/API/v2/find/` when the switch is off, the request failed, the answering worker has
 * not built its index, or nothing came back. Never rejects.
 */
const lookup = async (text: string): Promise<SearchAnswer> => {
    const omni = await searchOmni(text);
    if (omni) return { omni };
    return { legacy: await findInstruments(text).catch((): FindInstruments[] => []) };
};

// Only builds with trading compile the buying-power strip; elsewhere the condition is a constant
// false and the import, with its Vertex calls, is dropped (see next.config.js).
const QuickStats = process.env.NEXT_PUBLIC_TRADING_ENABLED === "1" ? dynamic(() => import("@/components/trading/quick-stats/QuickStats")) : () => null;

// Memoized: it takes no props here, and its Select walks every universe on each render (50-90 ms on a
// throttled phone), which the header did on every render of its own.
const UniverseSelect = memo(UniverseSelectBase);

interface PropsTypes {
    ticker: string;
    exchange: string;
    stock?: Stock;
    scrolledDown: boolean;
}

type Anchor = "top" | "left" | "bottom" | "right";

const Header: FC<PropsTypes> = ({ ticker, exchange, stock, scrolledDown }) => {
    const { handleAppSubscriberUpgrade } = useAppSubscriberUpgrade();
    // Trading buying-power strip: staff in the trading group, on a trading build only (src/lib/trading/access.ts).
    const tradingVisible = useTradingVisible();

    const textColor = useAtomValue(textColorState);
    const greyLighter = useAtomValue(greyLighterState);
    const theme = useAtomValue(themeState);
    const ortexColor = useAtomValue(ortexColorState);
    const greyDarker = useAtomValue(greyDarkerState);
    const whiteMedium = useAtomValue(whiteMediumState);
    const backgroundColor = useAtomValue(backgroundColorState);
    const user = useAtomValue(userState);
    const [searchHistory, setSearchHistory] = useState<FindInstruments[]>([]);
    const [state, setState] = useState({
        top: false,
        left: false,
        bottom: false,
        right: false,
    });

    const isMobile = useMediaQuery("(max-width: 768px)");
    const isTablet = useMediaQuery("(max-width: 1024px)");
    const is4k = useMediaQuery("(min-width: 3840px)");

    const [searchBox, setSearchBox] = useState(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [activeIndex, setActiveIndex] = useState<number>(null);
    const [instruments, setInstruments] = useState<FindInstruments[]>([]);
    // Rows for the rich omnisearch dropdown. `null` means this query is on the legacy dropdown.
    const [omniResults, setOmniResults] = useState<OmniResult[] | null>(null);
    const [searchValue, setSearchValue] = useState("");
    const [isVisitor, setIsVisitor] = useState<boolean>();
    const [showPlaceholder, setShowPlaceholder] = useState(true);
    const searchInputRef = useRef<HTMLInputElement>(null);
    // Only used with the omnisearch switch off, where the legacy endpoint keeps its 500 ms wait.
    const searchTimeoutRef = useRef<NodeJS.Timeout>(undefined);
    // The query in the box right now, so a late reply for an earlier keystroke is dropped.
    const latestQueryRef = useRef<string>("");
    const pipeRef = useRef<LatestSearch<SearchAnswer>>(undefined);
    // The local worker's rows for a text, merged with the server's when that lands.
    const localRowsRef = useRef<{ text: string; rows: OmniResult[] }>({ text: "", rows: [] });
    // The last text the server tier answered, so a late local answer cannot replace it.
    const answeredRef = useRef<string | null>(null);
    const focusTimeoutRef = useRef<NodeJS.Timeout>(undefined);
    const focusTimeout2Ref = useRef<NodeJS.Timeout>(undefined);
    const blurTimeoutRef = useRef<NodeJS.Timeout>(undefined);

    const { push } = useRouter();
    const router = useRouter();
    // Preloads the stock page a pick is likely to open, off the search's critical path (lib/omnisearch/stock-preload.ts).
    const preloaderRef = useRef<SearchPreloader>(undefined);

    const [chatModal, setChatModal] = useAtom(chatModalState);
    const [isChatMinimized, setIsChatMinimized] = useAtom(chatMinimizedState);
    const [showMinimizedTooltip, setShowMinimizedTooltip] = useState(false);
    const [loginRequiredOpen, setLoginRequiredOpen] = useState(false);

    // Shown over the empty box. Visual only (aria-hidden): the input's own aria-label is the accessible name.
    // The rotating word is pure CSS (Header.module.scss, .cycleWord); the first word is in the server HTML.
    const placeholder = useMemo(
        () => (
            <span className={styles.placeholderText} aria-hidden="true">
                {ticker && exchange ? (
                    `${exchange}:${ticker}`
                ) : (
                    <>
                        <span className={styles.cycle} style={{ "--cycle-words": SEARCH_WORDS.length, "--cycle-step": `${SEARCH_STEP_S}s` } as CSSProperties}>
                            Search{" "}
                            <span className={styles.cycleSlot}>
                                {SEARCH_WORDS.map((word, i) => (
                                    <span key={word} className={classNames(styles.cycleWord, word === "ORTEX" && styles.textLoopText)} style={{ animationDelay: `${searchWordDelayS(i)}s` }}>
                                        {word}
                                    </span>
                                ))}
                            </span>
                        </span>
                        <span className={styles.cycleStatic}>Search stocks, ETFs and more</span>
                    </>
                )}
            </span>
        ),
        [ticker, exchange]
    );

    useEffect(() => {
        if (user["subscription_plan"] === "ORTEX Visitor") {
            setIsVisitor(true);
        } else {
            setIsVisitor(false);
        }
    }, [user]);

    const handleUpgradeClick = () => {
        trackUpgradeClick(UPGRADE_LOCATIONS.HEADER, { plan: user?.subscription_plan });
        return user.subscription_plan === "ORTEX Visitor" ? push("/login?register=true") : handleAppSubscriberUpgrade();
    };

    const upgradeButtonText = {
        "ORTEX Free": "Upgrade",
        "ORTEX Basic": "Get Advanced",
        "ORTEX Visitor": "Create free account",
    };

    useEffect(() => {
        setSearchHistory(user?.settings?.search_settings.map((instrument) => ({ ...instrument, is_history: true })));
    }, [user?.settings?.search_settings]);

    const omniHistory = useMemo(() => (searchHistory ?? []).map(fromFindInstrument), [searchHistory]);
    const searchSource = isTablet ? "header-mobile" : "header";
    const rum = useMemo(() => new SearchRum(searchSource), [searchSource]);

    useEffect(() => {
        const preloader = createSearchPreloader(router);
        preloaderRef.current = preloader;
        return () => preloader.dispose();
        // One per mounted header; the preload itself uses the app's router instance (window.next.router).
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /** The stock page an omnisearch row opens, if it opens one. */
    const omniTarget = (row: OmniResult | undefined): StockTarget | null => (row ? browserStockTarget(routeForResult(row, user?.settings)) : null);
    /** The stock page a legacy row opens (the route the Enter handler pushes). */
    const legacyTarget = (row: FindInstruments | undefined): StockTarget | null =>
        row?.exchange && row.ticker ? browserStockTarget(`/s/${row.exchange}/${row.ticker}${getPreferredTabSuffix(readPreferredStockTab(user?.settings), row.security_type, row.exchange)}`) : null;
    const targetAt = (index: number | null): StockTarget | null => {
        if (index === null || index === undefined) return null;
        return omniResults ? omniTarget(omniResults[index]) : legacyTarget(instruments[index]);
    };

    // Put the server tier's answer on screen. Reassigned every render so it sees the current
    // history; the pipe calls whichever is current.
    const deliverRef = useRef<(text: string, answer: SearchAnswer) => void>(undefined);
    deliverRef.current = (text, answer) => {
        answeredRef.current = text;
        const local = localRowsRef.current.text === text ? localRowsRef.current.rows : [];
        if ("omni" in answer) {
            const rows = mergeResults(local, answer.omni, OMNI_LIMIT);
            setOmniResults(rows);
            setInstruments([]);
            if (rows.length) setActiveIndex(0);
            setLoading(false);
            rum.painted(text, "server", rows.length);
            rum.answered(text, rows.length);
            preloaderRef.current?.resultsArrived(text, omniTarget(rows[0]));
            return;
        }
        const data = answer.legacy;
        if (local.length) {
            // The local tier already shows a measure: keep it and list the legacy rows with it.
            // The measure stays first; `fromFindInstrument` marks rows as history, which these are not.
            const legacyRows = data.map((item) => {
                const row = fromFindInstrument(item);
                return { ...row, id: row.id.replace(/^history:/, "legacy:") };
            });
            const rows = mergeResults(legacyRows, local, OMNI_LIMIT);
            setOmniResults(rows);
            setInstruments([]);
            preloaderRef.current?.resultsArrived(text, omniTarget(rows[0]));
        } else {
            // Switch off, cold worker, error or no rows: the legacy dropdown, unchanged.
            setOmniResults(null);
            setInstruments([...data, ...searchHistory]);
            preloaderRef.current?.resultsArrived(text, legacyTarget(data[0]));
        }
        if (data.length || local.length) setActiveIndex(0);
        setLoading(false);
        rum.painted(text, "legacy", data.length);
        rum.answered(text, data.length + local.length);
    };

    useEffect(() => {
        const pipe = new LatestSearch<SearchAnswer>(
            lookup,
            (text, answer) => deliverRef.current?.(text, answer),
            () => setLoading(false)
        );
        pipeRef.current = pipe;
        return () => pipe.dispose();
    }, []);

    useEffect(() => () => rum.end(), [rum]);

    const closeSearch = () => {
        // The drawer's input has no blur handler, so a pick ends the RUM session here.
        rum.end();
        preloaderRef.current?.end();
        setSearchBox(false);
        setActiveIndex(null);
        searchInputRef.current?.blur();
    };

    // Runs from onChange with the text as it now stands. It used to run from onKeyDown,
    // which fires before the letter lands in the box: typing "volvo" searched "", "v" and
    // "volv" but never "volvo", so the dropdown showed the answer for "volv" until another
    // key event came along (measured on app.ortex.com, 2026-09-13).
    //
    // No wait with omnisearch on (ortex-gui#1197): the local worker answers measures on the
    // keystroke, and the server lookup goes out at once through the coalescing pipe
    // (`lib/omnisearch/client.ts`), which also drops answers for text no longer in the box.
    const getInstruments = (query: string) => {
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }
        latestQueryRef.current = query;
        // First: a new text cancels every preload candidate that has not started.
        preloaderRef.current?.typed(query);
        if (!query.trim()) {
            // An empty box shows history; it does not search for nothing.
            pipeRef.current?.cancel();
            setLoading(false);
            setInstruments(searchHistory);
            setOmniResults(isOmnisearchEnabled() && omniHistory.length ? omniHistory : null);
            return;
        }
        setLoading(true);
        rum.keystroke(query);
        // A new keystroke: the server has not answered it yet, whatever it answered before.
        answeredRef.current = null;
        if (!isOmnisearchEnabled()) {
            searchTimeoutRef.current = setTimeout(() => pipeRef.current?.query(query), searchDelayMs());
            return;
        }
        searchLocalSlice(query, OMNI_LIMIT).then((rows) => {
            if (!rows.length || latestQueryRef.current !== query || answeredRef.current === query) return;
            localRowsRef.current = { text: query, rows };
            setOmniResults(rows);
            setInstruments([]);
            setActiveIndex(0);
            rum.painted(query, "local", rows.length);
        });
        pipeRef.current?.query(query);
    };

    const handleKeyDown: KeyboardEventHandler<HTMLInputElement> = (event) => {
        switch (event.key) {
            case "Enter":
                if (omniResults && omniResults.length > 0 && activeIndex !== null) {
                    const selected = omniResults[activeIndex];
                    recordSearchClick(selected, latestQueryRef.current, activeIndex, searchSource);
                    rum.selected(activeIndex);
                    push(routeForResult(selected, user?.settings));
                    closeSearch();
                } else if (instruments.length > 0 && activeIndex !== null) {
                    const selected = instruments[activeIndex];
                    const tabSuffix = getPreferredTabSuffix(readPreferredStockTab(user?.settings), selected.security_type, selected.exchange);
                    rum.selected(activeIndex);
                    push(`/s/${selected.exchange || " ‎"}/${selected.ticker}${tabSuffix}`);
                    setSearchBox(false);
                    setActiveIndex(null);
                    searchInputRef.current?.blur();
                }
                break;
            case "ArrowDown": {
                const count = omniResults ? omniResults.length : instruments.length;
                if (count > 0 && activeIndex === null) {
                    setActiveIndex(0);
                    preloaderRef.current?.highlight(targetAt(0));
                } else if (count > 0 && activeIndex !== null) {
                    const next = activeIndex !== count - 1 ? activeIndex + 1 : 0;
                    setActiveIndex(next);
                    preloaderRef.current?.highlight(targetAt(next));
                }
                break;
            }
            case "ArrowUp":
                if ((omniResults ? omniResults.length : instruments.length) > 0 && activeIndex !== null) {
                    const next = activeIndex !== 0 ? activeIndex - 1 : null;
                    setActiveIndex(next);
                    preloaderRef.current?.highlight(targetAt(next));
                }
                break;
            default:
                setSearchBox(true);
                setActiveIndex(null);
                break;
        }
    };

    const handleInputFocus = useCallback(() => {
        // Queues an idle-time prefetch of the stock page's code, once per session; nothing is fetched here.
        preloaderRef.current?.focus();
        if (focusTimeoutRef.current) {
            clearTimeout(focusTimeoutRef.current);
        }
        if (focusTimeout2Ref.current) {
            clearTimeout(focusTimeout2Ref.current);
        }

        focusTimeoutRef.current = setTimeout(() => {
            setSearchBox(true);
            setShowPlaceholder(false);
        }, 250);

        focusTimeout2Ref.current = setTimeout(() => {
            // Results now arrive without a wait, so typing straight after focus can beat this
            // timer: history is only for a box that is still empty.
            if (latestQueryRef.current.trim()) return;
            setInstruments(searchHistory);
            setOmniResults(isOmnisearchEnabled() && omniHistory.length ? omniHistory : null);
        }, 500);
    }, [searchHistory, omniHistory]);

    const handleInputBlur = useCallback(() => {
        if (blurTimeoutRef.current) {
            clearTimeout(blurTimeoutRef.current);
        }

        blurTimeoutRef.current = setTimeout(() => {
            pipeRef.current?.cancel();
            latestQueryRef.current = "";
            preloaderRef.current?.end();
            rum.end();
            setSearchValue("");
            setSearchBox(false);
            setInstruments([]);
            setOmniResults(null);
            setShowPlaceholder(true);
        }, 250);
    }, [rum]);

    const toggleDrawer = (anchor: Anchor, open: boolean) => (event: React.KeyboardEvent | React.MouseEvent) => {
        if (event.type === "keydown" && ((event as React.KeyboardEvent).key === "Tab" || (event as React.KeyboardEvent).key === "Shift")) {
            return;
        }
        // Closing the drawer without a pick is a dismissal: end the RUM session.
        if (!open) rum.end();
        setSearchBox(true);
        setState({ ...state, [anchor]: open });
    };

    useEffect(() => {
        if (isTablet || isMobile) {
            const handleRouteChange = () => {
                handleInputBlur();
                setState((prevState) => ({ ...prevState, top: false }));
            };

            router.events.on("routeChangeStart", handleRouteChange);

            return () => {
                router.events.off("routeChangeStart", handleRouteChange);
            };
        }
    }, [isMobile, isTablet, handleInputBlur]);

    useEffect(() => {
        if (isChatMinimized) {
            setShowMinimizedTooltip(true);
            const timeout = setTimeout(() => {
                setShowMinimizedTooltip(false);
            }, 3000); // Show for 3 seconds

            return () => clearTimeout(timeout);
        } else {
            setShowMinimizedTooltip(false);
        }
    }, [isChatMinimized]);

    useEffect(() => {
        return () => {
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
            if (focusTimeoutRef.current) {
                clearTimeout(focusTimeoutRef.current);
            }
            if (focusTimeout2Ref.current) {
                clearTimeout(focusTimeout2Ref.current);
            }
            if (blurTimeoutRef.current) {
                clearTimeout(blurTimeoutRef.current);
            }
        };
    }, []);

    const handleGoToLogin = useCallback(() => {
        setLoginRequiredOpen(false);
        router.push("/login");
    }, [router]);

    return (
        <>
            <header
                className={styles.Header}
                style={{
                    backgroundColor: isMobile && !scrolledDown ? "transparent" : backgroundColor,
                    visibility: user.email ? "visible" : "hidden",
                    borderBottom: isMobile ? "none" : `1px solid ${theme === "dark" ? greyDarker : theme === "light" ? whiteMedium : "unset"}`,
                    transition: "background-color 0.5s ease",
                }}
            >
                <div>
                    <Menu />
                    {isMobile ? (
                        <Box>
                            <Link passHref legacyBehavior href="/">
                                <IconButton className={styles.logo}>
                                    <Image unoptimized src="/assets/ortex-icon.png" alt="ortex-logo" width={20} height={20} priority />
                                </IconButton>
                            </Link>
                        </Box>
                    ) : (
                        <Box sx={{ cursor: "pointer" }}>
                            <Link href="/">
                                <Image unoptimized src="/assets/ortex-logo-h.png" alt="ortex-logo-h" width={145} height={30} priority />
                            </Link>{" "}
                        </Box>
                    )}

                    <div className={classNames(styles.middleSection, searchBox ? styles.searchStarted : "")}>
                        <Box>
                            {isMobile || isTablet ? (
                                <div className={styles.mobileSearch}>
                                    <IconButton aria-label="search" style={{ color: textColor }} onClick={toggleDrawer("top", !state["top"])}>
                                        <SearchIcon />
                                    </IconButton>

                                    <Drawer
                                        anchor="top"
                                        open={state["top"]}
                                        onClose={toggleDrawer("top", false)}
                                        sx={{
                                            zIndex: 6,
                                            "&.MuiDrawer-anchorTop > .MuiDrawer-paper": {
                                                top: `calc(40px + env(safe-area-inset-top) + ${is4k ? "40px" : "25px"})`,
                                            },
                                        }}
                                        className={styles.expo}
                                    >
                                        <div className={styles.mobileSearchDrawer}>
                                            <div className={styles.mobileSearchDrawerContent}>
                                                <FormControl size="small" fullWidth sx={{ mt: 1, mb: 1, display: "flex", flex: 1, width: "unset", border: 0 }}>
                                                    <OutlinedInput
                                                        type="text"
                                                        inputRef={searchInputRef}
                                                        onKeyDown={handleKeyDown}
                                                        onFocus={handleInputFocus}
                                                        autoCapitalize="none"
                                                        onChange={(event) => {
                                                            setSearchValue(event.target.value);
                                                            setSearchBox(true);
                                                            getInstruments(event.target.value);
                                                        }}
                                                        value={searchValue}
                                                        sx={{
                                                            width: "100%",
                                                        }}
                                                        id="outlined-adornment-amount"
                                                        className={styles.searchInput}
                                                        startAdornment={
                                                            <InputAdornment position="start">
                                                                <SearchIcon htmlColor={greyLighter} />
                                                            </InputAdornment>
                                                        }
                                                        autoComplete="off"
                                                        name="search"
                                                        inputProps={{ "aria-label": "Search ORTEX" }}
                                                    />
                                                    {showPlaceholder && <div className={styles.placeholder}>{placeholder}</div>}
                                                </FormControl>
                                                {searchBox &&
                                                    (omniResults
                                                        ? omniResults.length > 0 && (
                                                              <OmniAutocomplete
                                                                  results={omniResults}
                                                                  activeIndex={activeIndex}
                                                                  query={latestQueryRef.current}
                                                                  source={searchSource}
                                                                  onMouseEnter={() => setActiveIndex(null)}
                                                                  onRowHover={(index) => preloaderRef.current?.highlight(index === null ? null : omniTarget(omniResults[index]))}
                                                                  onSelect={(index) => {
                                                                      rum.selected(index);
                                                                      closeSearch();
                                                                  }}
                                                              />
                                                          )
                                                        : instruments.length > 0 && (
                                                              <HeaderAutocomplete
                                                                  instruments={instruments}
                                                                  activeIndex={activeIndex}
                                                                  onMouseEnter={() => setActiveIndex(null)}
                                                                  onRowHover={(row) => preloaderRef.current?.highlight(legacyTarget(row ?? undefined))}
                                                              />
                                                          ))}
                                                {loading && (
                                                    <div className={styles.loader}>
                                                        <CircularProgress size={15} />
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </Drawer>
                                </div>
                            ) : (
                                <div className={styles.search}>
                                    <div className={styles.backdrop}></div>
                                    <FormControl size="small" fullWidth sx={{ mt: 1, mb: 1, display: "flex", flex: 1, width: "unset", border: 0 }}>
                                        <OutlinedInput
                                            id="outlined-adornment-amount"
                                            value={searchValue}
                                            className={styles.searchInput}
                                            onChange={(event) => {
                                                setSearchValue(event.target.value);
                                                setSearchBox(true);
                                                getInstruments(event.target.value);
                                            }}
                                            startAdornment={
                                                <InputAdornment position="start">
                                                    <SearchIcon htmlColor={textColor} />
                                                </InputAdornment>
                                            }
                                            onKeyDown={handleKeyDown}
                                            onFocus={handleInputFocus}
                                            onBlur={handleInputBlur}
                                            autoCapitalize="none"
                                            autoComplete="off"
                                            name="search"
                                            inputProps={{ "aria-label": "Search ORTEX" }}
                                        />
                                        {showPlaceholder && <div className={styles.placeholder}>{placeholder}</div>}
                                    </FormControl>
                                    {searchBox &&
                                        (omniResults
                                            ? omniResults.length > 0 && (
                                                  <OmniAutocomplete
                                                      results={omniResults}
                                                      activeIndex={activeIndex}
                                                      query={latestQueryRef.current}
                                                      source={searchSource}
                                                      onMouseEnter={() => setActiveIndex(null)}
                                                      onRowHover={(index) => preloaderRef.current?.highlight(index === null ? null : omniTarget(omniResults[index]))}
                                                      onSelect={(index) => {
                                                          rum.selected(index);
                                                          closeSearch();
                                                      }}
                                                  />
                                              )
                                            : instruments.length > 0 && (
                                                  <HeaderAutocomplete
                                                      instruments={instruments}
                                                      activeIndex={activeIndex}
                                                      onMouseEnter={() => setActiveIndex(null)}
                                                      onRowHover={(row) => preloaderRef.current?.highlight(legacyTarget(row ?? undefined))}
                                                  />
                                              ))}
                                </div>
                            )}
                        </Box>
                        <div className={styles.universe}>
                            <UniverseSelect />
                        </div>
                    </div>
                    {["ORTEX Free", "ORTEX Basic", "ORTEX Visitor"].includes(user?.subscription_plan) && !isMobile && (
                        <Button variant="outlined" size="small" onClick={handleUpgradeClick} sx={{ minWidth: "80px" }}>
                            {upgradeButtonText[user?.subscription_plan]}
                        </Button>
                    )}
                    {tradingVisible && !isMobile && <QuickStats />}

                    <Tooltip {...(showMinimizedTooltip ? { open: true } : {})} arrow title={showMinimizedTooltip ? "Continue the conversation here" : "ORTEX AI"}>
                        <IconButton
                            aria-label="ORTEX AI"
                            className={styles.askOrtexAI}
                            style={{ color: textColor, marginLeft: "var(--header-gap, 10px)" }}
                            onClick={() => {
                                if (chatModal.open && !isChatMinimized) {
                                    // Chat is open and visible - close it
                                    setChatModal({ open: false, initialMessage: undefined, startingModel: undefined, chatModel: undefined });
                                } else if (chatModal.open && isChatMinimized) {
                                    // Chat is minimized - un-minimize it
                                    setIsChatMinimized(false);
                                } else {
                                    // Chat is closed - open it (or show login dialog for visitors)
                                    if (isVisitor) {
                                        setLoginRequiredOpen(true);
                                        return;
                                    }
                                    setChatModal({ open: true, initialMessage: undefined, startingModel: undefined, chatModel: undefined });
                                    setIsChatMinimized(false);
                                }
                            }}
                        >
                            <AutoAwesomeIcon color="action" style={{ color: textColor }} />
                            <span className={styles.askOrtexGlow} aria-hidden="true">
                                <AutoAwesomeIcon color="action" style={{ color: textColor }} />
                            </span>
                        </IconButton>
                    </Tooltip>
                    {/* Phones had no way to sign up from the header at all (the button
                        above is desktop-only). A signed-out visitor has no custom views
                        and no notifications of their own, so on a phone the sign-up button
                        takes those two icons' slots (the row is already full at 375px). */}
                    {isVisitor && isMobile ? (
                        <Button variant="contained" size="small" onClick={handleUpgradeClick} sx={{ minWidth: 0, flexShrink: 0, px: 1.25, mx: 0.5, fontSize: "12px", fontWeight: 700, whiteSpace: "nowrap", textTransform: "none" }}>
                            Sign up
                        </Button>
                    ) : (
                        <>
                            <HeaderCustomViews />
                            <Notifications />
                        </>
                    )}
                    <div className={styles.user}>
                        <UserMenu firstName={user.first_name} lastName={user.last_name} isVisitor={isVisitor} />
                    </div>
                </div>
            </header>

            <Dialog
                open={loginRequiredOpen}
                onClose={() => setLoginRequiredOpen(false)}
                slotProps={{
                    backdrop: { className: chatInputStyles.loginDialogBackdrop },

                    paper: {
                        className: classNames(chatInputStyles.loginDialogPaper, theme === "dark" && chatInputStyles.dark),
                    },
                }}
            >
                <DialogContent className={chatInputStyles.loginDialogContent} sx={{ color: textColor }}>
                    <Box className={chatInputStyles.loginDialogIconWrap} sx={{ "& .MuiSvgIcon-root": { color: ortexColor } }}>
                        <ChatBubbleOutlineRoundedIcon />
                    </Box>
                    <Typography variant="h6" className={chatInputStyles.loginDialogTitle}>
                        Sign in to chat
                    </Typography>
                    <Typography variant="body2" className={chatInputStyles.loginDialogBody}>
                        Create a free account or log in to chat with the ORTEX assistant and get answers instantly.
                    </Typography>
                </DialogContent>
                <DialogActions className={chatInputStyles.loginDialogActions}>
                    <Button
                        fullWidth
                        variant="contained"
                        onClick={handleGoToLogin}
                        className={chatInputStyles.loginDialogPrimaryBtn}
                        sx={{ bgcolor: `${ortexColor} !important`, color: "#fff", "&:hover": { bgcolor: `${ortexColor} !important`, filter: "brightness(1.1)" } }}
                    >
                        Sign in
                    </Button>
                    <Button variant="text" onClick={() => setLoginRequiredOpen(false)} className={chatInputStyles.loginDialogSecondaryBtn} sx={{ color: textColor }}>
                        Not now
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default Header;
